import React from 'react';
import { clsx } from 'clsx';

export const SlugBadge: React.FC<{ children: string }> = ({ children }) => (
  <span
    className={clsx(
      'inline-flex items-center rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5',
      'font-mono text-[11px] font-medium uppercase tracking-wide text-slate-600'
    )}
    title="Identificador (slug)"
  >
    {children}
  </span>
);

const roleStyles: Record<string, string> = {
  ADMIN: 'border-indigo-200 bg-indigo-50 text-indigo-800 ring-indigo-100',
  MANAGER: 'border-slate-200 bg-slate-100 text-slate-700 ring-slate-200',
  EMPLOYEE: 'border-slate-200 bg-white text-slate-600 ring-slate-200',
  PLATFORM_OWNER: 'border-violet-200 bg-violet-50 text-violet-800 ring-violet-100',
};

const roleLabels: Record<string, string> = {
  ADMIN: 'Administrador',
  MANAGER: 'Gerente',
  EMPLOYEE: 'Empleado',
  PLATFORM_OWNER: 'Plataforma',
};

export const RoleBadge: React.FC<{ role: string; highlightAdmin?: boolean }> = ({
  role,
  highlightAdmin = true,
}) => {
  const isAdmin = role === 'ADMIN';
  const style = roleStyles[role] ?? 'border-slate-200 bg-slate-50 text-slate-700 ring-slate-200';
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1',
        style,
        highlightAdmin && isAdmin && 'ring-2 ring-indigo-300/60 shadow-sm'
      )}
    >
      {roleLabels[role] ?? role}
    </span>
  );
};

export const TenantStatusBadge: React.FC<{ isActive: boolean }> = ({ isActive }) =>
  isActive ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-800 ring-1 ring-emerald-200">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden />
      Activa
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600 ring-1 ring-slate-200">
      <span className="h-1.5 w-1.5 rounded-full bg-slate-400" aria-hidden />
      Suspendida
    </span>
  );

export const UserActiveBadge: React.FC<{ isActive: boolean }> = ({ isActive }) =>
  isActive ? (
    <span className="text-xs font-medium text-emerald-700">Activo</span>
  ) : (
    <span className="text-xs font-medium text-slate-500">Inactivo</span>
  );

const incidentStatusStyles: Record<string, string> = {
  NEW: 'border-sky-200 bg-sky-50 text-sky-900 ring-sky-100',
  REVIEWING: 'border-violet-200 bg-violet-50 text-violet-900 ring-violet-100',
  IN_PROGRESS: 'border-amber-200 bg-amber-50 text-amber-900 ring-amber-100',
  RESOLVED: 'border-emerald-200 bg-emerald-50 text-emerald-900 ring-emerald-100',
  CLOSED: 'border-slate-200 bg-slate-100 text-slate-700 ring-slate-200',
  REJECTED: 'border-rose-200 bg-rose-50 text-rose-900 ring-rose-100',
};

const incidentStatusLabels: Record<string, string> = {
  NEW: 'Nuevo',
  REVIEWING: 'En revisión',
  IN_PROGRESS: 'En curso',
  RESOLVED: 'Resuelto',
  CLOSED: 'Cerrado',
  REJECTED: 'Rechazado',
};

export const IncidentStatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span
    className={clsx(
      'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1',
      incidentStatusStyles[status] ?? 'border-slate-200 bg-slate-50 text-slate-700 ring-slate-200'
    )}
  >
    {incidentStatusLabels[status] ?? status}
  </span>
);

const severityStyles: Record<string, string> = {
  LOW: 'border-slate-200 bg-slate-50 text-slate-700',
  MEDIUM: 'border-amber-200 bg-amber-50 text-amber-900',
  HIGH: 'border-orange-200 bg-orange-50 text-orange-900',
  CRITICAL: 'border-rose-300 bg-rose-50 text-rose-950',
};

const severityLabels: Record<string, string> = {
  LOW: 'Baja',
  MEDIUM: 'Media',
  HIGH: 'Alta',
  CRITICAL: 'Crítica',
};

export const IncidentSeverityBadge: React.FC<{ severity: string }> = ({ severity }) => (
  <span
    className={clsx(
      'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1',
      severityStyles[severity] ?? 'border-slate-200 bg-slate-50 text-slate-700'
    )}
  >
    {severityLabels[severity] ?? severity}
  </span>
);

const categoryLabels: Record<string, string> = {
  ERROR: 'Error',
  INCIDENT: 'Incidencia',
  SUPPORT: 'Soporte',
};

export const IncidentCategoryBadge: React.FC<{ category: string }> = ({ category }) => (
  <span className="inline-flex items-center rounded-md border border-slate-200 bg-white px-2 py-0.5 text-xs font-medium text-slate-700">
    {categoryLabels[category] ?? category}
  </span>
);

const planTierStyles: Record<string, string> = {
  BASIC: 'border-slate-200 bg-slate-50 text-slate-700 ring-slate-200',
  PROFESSIONAL: 'border-amber-200 bg-amber-50 text-amber-900 ring-amber-100',
  PREMIUM: 'border-violet-200 bg-violet-50 text-violet-900 ring-violet-100',
};

export const PlanTierBadge: React.FC<{ tier?: string | null; label: string }> = ({ tier, label }) => (
  <span
    className={clsx(
      'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1',
      planTierStyles[tier ?? ''] ?? 'border-slate-200 bg-slate-50 text-slate-700 ring-slate-200'
    )}
    title={tier ? `Plan ${tier}` : undefined}
  >
    {label}
  </span>
);
